import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** How many modals are open, so the page behind stays inert until the last one closes. */
let open = 0;

function setPageInert(inert: boolean) {
  const root = document.getElementById("root");
  if (!root) return;
  if (inert) root.setAttribute("inert", "");
  else root.removeAttribute("inert");
}

/**
 * A modal dialog's keyboard and focus behaviour: focus moves in when it opens and back to where
 * it was when it closes, Tab stays inside, Escape closes it, and the page behind is inert. The
 * dialog itself is rendered in a portal on the body, outside the page it covers.
 */
export function useModal(
  ref: RefObject<HTMLElement>,
  onClose: () => void,
  initialFocus?: RefObject<HTMLElement>,
) {
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const box = ref.current;
    open += 1;
    setPageInert(true);
    const first = initialFocus?.current ?? box?.querySelector<HTMLElement>(FOCUSABLE) ?? box;
    first?.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (!box) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      const head = items[0];
      const tail = items[items.length - 1];
      if (!head || !tail) {
        e.preventDefault();
        return;
      }
      if (
        e.shiftKey &&
        (document.activeElement === head || !box.contains(document.activeElement))
      ) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && document.activeElement === tail) {
        e.preventDefault();
        head.focus();
      }
    };
    box?.addEventListener("keydown", onKey);

    return () => {
      box?.removeEventListener("keydown", onKey);
      open = Math.max(0, open - 1);
      if (open === 0) setPageInert(false);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
    // Runs once per opening; the latest onClose is read through the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
